import React, { FC, useState } from 'react'
import { useSelector } from 'react-redux'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { Box } from 'transfix-ui/core'
import { LOAD_STATE } from 'apps/fleet-planner/constants'
import { useUserContext } from 'apps/fleet-planner/context'
import { useQuickBooksApi, useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { ILoad } from 'apps/fleet-planner/types'
import { customersListSelector } from 'apps/fleet-planner/utils/selectors'
import { Button, Card } from 'transfix-ui'
import { QuickBooksInfoCard } from './QuickBooksInfoCard'

const isCreateInvoiceVisible = (status: LOAD_STATE): boolean => {
  return [
    LOAD_STATE.Delivered,
    LOAD_STATE.Completed,
    LOAD_STATE.Cancelled,
    LOAD_STATE.TONU,
    LOAD_STATE.Claim,
    LOAD_STATE.Invoiced,
  ].includes(status)
}

export interface ILoadDetailsInvoiceProps
  extends Pick<ILoad, 'id' | 'status' | 'customer' | 'quickbooksInvoiceId' | 'rate'> {}

/**
 * Contains logic around displaying different data and options
 * based on the load status, customer, and quickbooks import status.
 * Used in LoadDetails and LoadDetailsModal components
 */
export const LoadDetailsInvoice: FC<ILoadDetailsInvoiceProps> = ({
  customer: customerProp,
  id: loadId,
  quickbooksInvoiceId,
  rate,
  status,
}) => {
  const { trackEvent } = useFMSAnalytics()
  const [isCreatingInvoice, setCreatingInvoice] = useState<boolean>(false)
  const { createQuickbooksInvoice, error, getQuickBooksGrantUrl, isLoading } = useQuickBooksApi()
  const { getUser } = useUserContext()
  const { hasQuickbooksEnabled = false } = getUser() || {}
  const isValidStatus = isCreateInvoiceVisible(status)

  // Use redux for this info, since it may not be stored on the load object
  const customers = useSelector(customersListSelector)
  const customer = customers.find(c => c.id === customerProp?.id)
  const isCustomerImported = !!customer?.importedFromQuickbooks

  const hasInvoiceId = quickbooksInvoiceId !== null && typeof quickbooksInvoiceId !== 'undefined'

  // Is returned as a string from backend
  const hasValidRate = typeof rate !== 'undefined' && rate !== null && parseFloat(`${rate}`) > 0

  const createInvoiceHandler = async () => {
    trackEvent('Quickbooks', 'QUICKBOOKS_CREATE_INVOICE_BUTTON_CLICK')
    if (!loadId) return
    const res = await createQuickbooksInvoice(loadId)
    if (res) setCreatingInvoice(true)
  }

  const connectQuickBooksHandler = async () => {
    trackEvent('Quickbooks', 'QUICKBOOKS_CONNECT_ACCOUNT_BUTTON_CLICK')
    const url = await getQuickBooksGrantUrl()
    if (url) window.location.href = url
  }

  const isCreateButtonDisabled = !hasValidRate || !isCustomerImported

  const getErrorMessage = (): string => {
    const errors: string[] = []
    if (!hasValidRate) errors.push('enter a valid rate')
    if (!isCustomerImported) errors.push('add a QuickBooks customer')
    return `Please ${errors.join(' and ')} to this load to enable one-click invoicing.`
  }

  return (
    <>
      {isValidStatus ? (
        <Card className='mt4 mb4' dataTest='quickbooks-container'>
          <div className='flex flex-row items-center mb4'>
            <span className='body-medium'>Invoice</span>
          </div>
          {/**
           * User has completed auth integration flow
           */}
          {hasQuickbooksEnabled && (
            <Box>
              {/**
               * Load does not yet have an invoice ID associated,
               * and the user has not yet triggered the create flow
               */}
              {!isCreatingInvoice && !hasInvoiceId && (
                <Box>
                  <Button
                    color='secondary'
                    onClick={createInvoiceHandler}
                    data-test='quickbooks-create-invoice-button'
                    disabled={isCreateButtonDisabled}>
                    Create Invoice in QuickBooks
                  </Button>
                  {/**
                   * Load has a customer selected that is not a valid QuickBooks customer
                   * @note This condition also disables the button
                   */}
                  {isCreateButtonDisabled && <Box mt={1}>{getErrorMessage()}</Box>}
                </Box>
              )}
              {/**
               * User has triggered the create flow
               */}
              {isCreatingInvoice && (
                <Box>
                  <InlineMessage
                    type={INLINE_MESSAGE_VARIANTS.INFO}
                    message={
                      <>
                        <strong>Your invoice is being created in QuickBooks.</strong> This may take
                        a few minutes...{' '}
                      </>
                    }
                  />
                </Box>
              )}
            </Box>
          )}
          {/**
           * User has not completed QuickBooks auth integration flow
           */}
          {!hasQuickbooksEnabled && (
            <Box>
              <Button
                className='btn-text-link-b'
                onClick={connectQuickBooksHandler}
                data-test='quickbooks-connect-button'
                disabled={isLoading}>
                Connect your QuickBooks account
              </Button>{' '}
              to enable one-click invoicing.
              {error && (
                <Box mt={2}>
                  <InlineMessage message={error} type={INLINE_MESSAGE_VARIANTS.ALERT} />
                </Box>
              )}
            </Box>
          )}
          {/**
           * If the user has quick books enabled, and
           * this load has an invoice associated with it
           */}
          {hasQuickbooksEnabled && hasInvoiceId && (
            <Box mt={2}>
              <QuickBooksInfoCard quickbooksInvoiceId={quickbooksInvoiceId} />
              {error && (
                <Box mt={2}>
                  <InlineMessage message={error} type={INLINE_MESSAGE_VARIANTS.ALERT} />
                </Box>
              )}
            </Box>
          )}
        </Card>
      ) : (
        ''
      )}
    </>
  )
}
